/* eslint-disable yola/jsx-a11y/label-has-for */
import React, { useCallback, useRef } from 'react';
import classNames from 'classnames';
import VisuallyHidden from '../../../visually-hidden';
import ActionButton from '../../../action-button';
import Paragraph from '../../../paragraph';
import IconButton from '../../../icon-button';
import FieldStatus from '../../../field-status';
import { APPEARANCE as FIELD_STATUS_APPEARANCES } from '../../../field-status/constants';
import { FILE_PICKER_PROPS } from './types';
import { getFileExtension } from './helpers/get-file-extension';
import { SIZES } from '../../constants';

const FilePicker = ({
  label,
  placeholder = '',
  statusText = '',
  invalid = false,
  fileItemName = '',
  accept = 'all',
  multiple = false,
  disabled = false,
  size = SIZES.small,
  onClear = Function.prototype,
  onChange = null,
  onError = Function.prototype,
  onClick = Function.prototype,
}) => {
  const inputRef = useRef(null);
  const isFilePicked = Boolean(fileItemName);

  const classes = classNames('ws-ui-file-picker', `ws-ui-file-picker--${size}`, {
    'ws-ui-file-picker--disabled': disabled,
  });

  const handleChange = useCallback(
    ({ target: { files } }) => {
      const fileList = Array.from(files);

      if (!fileList.length) return;

      if (accept === 'all') {
        onChange(fileList);
      }

      const extensionsList = fileList.map((file) => getFileExtension(file.name.toLowerCase()));
      const wrongExtensions = extensionsList.filter((extension) => !accept.includes(extension));
      const supportedFilesList = fileList.filter(
        (file) => !wrongExtensions.includes(getFileExtension(file.name.toLowerCase()))
      );
      const dismissedFilesList = fileList.filter((file) =>
        wrongExtensions.includes(getFileExtension(file.name.toLowerCase()))
      );

      if (wrongExtensions.length) {
        onError(wrongExtensions, {
          supportedFilesList,
          dismissedFilesList,
        });
        return;
      }

      onChange(fileList);
    },
    [onError, onChange, accept]
  );

  const handleClick = useCallback(() => {
    if (onChange) inputRef.current.click();

    onClick();
  }, [onClick, onChange]);

  const handleClear = useCallback(() => {
    inputRef.current.value = '';
    onClear();
  }, [onClear]);

  return (
    <div className={classes}>
      <div className="ws-ui-file-picker__action-area">
        <label className="ws-ui-file-picker__label">
          <VisuallyHidden>
            <input
              type="file"
              ref={inputRef}
              disabled={disabled}
              onChange={handleChange}
              multiple={multiple}
              tabIndex="-1"
            />
          </VisuallyHidden>
        </label>
        <div className="ws-ui-file-picker__button">
          <ActionButton
            label={label}
            fontWeight="regular"
            size={size}
            disabled={disabled}
            onClick={handleClick}
          />
        </div>
        <div className="ws-ui-file-picker__title">
          <Paragraph isClipped appearance={disabled ? 'low-emphasis' : 'medium-emphasis'}>
            {isFilePicked ? fileItemName : placeholder}
          </Paragraph>
        </div>
        {isFilePicked && (
          <div className="ws-ui-file-picker__clear">
            <IconButton
              glyph="clear-search"
              size="medium"
              transparent
              disabled={disabled}
              onClick={handleClear}
            />
          </div>
        )}
      </div>
      {statusText && (
        <FieldStatus
          text={statusText}
          appearance={invalid ? FIELD_STATUS_APPEARANCES.error : FIELD_STATUS_APPEARANCES.default}
        />
      )}
    </div>
  );
};

FilePicker.propTypes = FILE_PICKER_PROPS;

export default FilePicker;
